import { useCallback, useEffect, useRef, useState } from "react";
import { PushPinIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { getAPI, onWorkspaceEvent } from "../services/workspaceService";
import type {
  SelectedFeatureInfo,
  SelectionInfo,
} from "../tools/selectionApiTypes";

interface SelectionContextBannerProps {
  isReady: boolean;
  onSelectionChange: (selection: SelectionInfo | null) => void;
}

/** Workspace API event fired when the host selection changes. */
const SELECTION_EVENT = "selection.changed";

/** Stable key for a feature (prefer guid, fall back to featureId + name). */
const featureKey = (f: SelectedFeatureInfo): string =>
  f.guid || `${f.featureId}:${f.name}`;

function parseSelectionData(raw: unknown): SelectionInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  const count =
    typeof obj.count === "number"
      ? obj.count
      : typeof obj.Count === "number"
        ? obj.Count
        : 0;

  // C# sends "items", TS type expects "features" — normalise
  const items = (obj.features ?? obj.items ?? obj.Items ?? []) as Array<
    Record<string, unknown>
  >;

  return {
    count,
    features: items.map((it) => ({
      type: (it.type ?? it.Type ?? "Feature") as string,
      featureId: (it.featureId ?? it.FeatureId ?? 0) as number,
      guid: (it.guid ?? it.Guid ?? "") as string,
      name: (it.name ?? it.Name ?? "") as string,
      featureTypeId: (it.featureTypeId ?? it.FeatureTypeId ?? 0) as number,
      featureTypeName: (it.featureTypeName ??
        it.FeatureTypeName ??
        "") as string,
      taskTypeTag: (it.taskTypeTag ?? it.TaskTypeTag ?? undefined) as
        | string
        | undefined,
      taskTypeName: (it.taskTypeName ?? it.TaskTypeName ?? undefined) as
        | string
        | undefined,
    })),
  };
}

export function SelectionContextBanner({
  isReady,
  onSelectionChange,
}: SelectionContextBannerProps) {
  /** Raw host selection (may be null when nothing is selected). */
  const [hostSelection, setHostSelection] = useState<SelectionInfo | null>(
    null,
  );

  /** Features explicitly pinned by the user (keyed by featureKey). */
  const [pinnedMap, setPinnedMap] = useState<Map<string, SelectedFeatureInfo>>(
    () => new Map(),
  );
  /** Keys of features the user has explicitly removed / dismissed. */
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(
    () => new Set(),
  );

  // ── Derived: effective features = (host selection ∪ pinned) − dismissed ──
  const selectedKeys = new Set((hostSelection?.features ?? []).map(featureKey));

  const effectiveFeatures: (SelectedFeatureInfo & { pinned: boolean })[] = [];
  const seen = new Set<string>();

  // Current host selection first
  for (const f of hostSelection?.features ?? []) {
    const key = featureKey(f);
    if (dismissedKeys.has(key)) continue;
    seen.add(key);
    effectiveFeatures.push({ ...f, pinned: pinnedMap.has(key) });
  }
  // Then pinned items not in the current selection
  for (const [key, f] of pinnedMap) {
    if (seen.has(key) || dismissedKeys.has(key)) continue;
    seen.add(key);
    effectiveFeatures.push({ ...f, pinned: true });
  }

  // ── Notify parent whenever effective set changes ──
  const prevEffectiveRef = useRef<string>("");
  const effectiveSignature = effectiveFeatures
    .map((f) => featureKey(f))
    .sort()
    .join(",");

  useEffect(() => {
    if (effectiveSignature === prevEffectiveRef.current) return;
    prevEffectiveRef.current = effectiveSignature;

    if (effectiveFeatures.length === 0) {
      onSelectionChange(null);
    } else {
      onSelectionChange({
        count: effectiveFeatures.length,
        features: effectiveFeatures,
      });
    }
    // effectiveSignature is the stable dep; effectiveFeatures is derived from it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSignature, onSelectionChange]);

  // ── Handlers ──
  const handlePinnedChange = useCallback(
    (pinned: boolean) => {
      const selectedFeatures = hostSelection?.features ?? [];
      if (selectedFeatures.length === 0) return;

      setPinnedMap((prev) => {
        const next = new Map(prev);
        for (const feature of selectedFeatures) {
          const key = featureKey(feature);
          if (pinned) next.set(key, feature);
          else next.delete(key);
        }
        return next;
      });
    },
    [hostSelection],
  );

  const isSelectionPinned =
    (hostSelection?.features.length ?? 0) > 0 &&
    (hostSelection?.features ?? []).every((feature) =>
      pinnedMap.has(featureKey(feature)),
    );

  const handleItemPinnedChange = useCallback(
    (feature: SelectedFeatureInfo, pinned: boolean) => {
      const key = featureKey(feature);
      setPinnedMap((prev) => {
        const next = new Map(prev);
        if (pinned) next.set(key, feature);
        else next.delete(key);
        return next;
      });
    },
    [],
  );

  const handleRemove = useCallback((f: SelectedFeatureInfo) => {
    const key = featureKey(f);
    setPinnedMap((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    setDismissedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // When host selection changes, clear dismissed keys so re-selected items reappear
  const prevHostRef = useRef<string>("");
  const updateHostSelection = useCallback((info: SelectionInfo | null) => {
    const sig = (info?.features ?? []).map(featureKey).sort().join(",");
    if (sig !== prevHostRef.current) {
      prevHostRef.current = sig;
      setDismissedKeys(new Set());
    }
    setHostSelection(info);
  }, []);

  // Poll current selection once the workspace API is ready
  useEffect(() => {
    if (!isReady) return;

    (async () => {
      try {
        const api = getAPI();
        const selectionApi = api?.selectionApi;
        if (!selectionApi?.getCurrentSelection) {
          console.warn(
            "[SelectionBanner] selectionApi.getCurrentSelection not available on this host",
          );
          return;
        }

        const raw: string = await selectionApi.getCurrentSelection();
        const result = typeof raw === "string" ? JSON.parse(raw) : raw;
        const info = parseSelectionData(result);
        updateHostSelection(info && info.count > 0 ? info : null);
      } catch (err) {
        console.error("[SelectionBanner] Failed to poll selection:", err);
      }
    })();
  }, [isReady, updateHostSelection]);

  // Subscribe to selection.changed events
  useEffect(() => {
    const unsubscribe = onWorkspaceEvent((event, data) => {
      if (event !== SELECTION_EVENT) return;

      const info = parseSelectionData(data);
      updateHostSelection(info && info.count > 0 ? info : null);
    });
    return unsubscribe;
  }, [updateHostSelection]);

  // Nothing to show — only hide when host has no selection AND nothing is pinned
  const hasHostSelection = hostSelection !== null && hostSelection.count > 0;
  if (
    effectiveFeatures.length === 0 &&
    !hasHostSelection &&
    pinnedMap.size === 0
  )
    return null;

  return (
    <section className="border-b border-border bg-background px-2 pb-2 pt-1.5 font-sans text-foreground">
      <div className="mb-1.5 flex min-h-6 items-center gap-2 text-xs">
        <span className="font-semibold">
          Selected ({effectiveFeatures.length})
        </span>
        {pinnedMap.size > 0 && (
          <span className="text-muted-foreground">{pinnedMap.size} pinned</span>
        )}
        <div className="ml-auto">
          <Button
            variant="ghost"
            size="xs"
            disabled={!hasHostSelection}
            aria-pressed={isSelectionPinned}
            onClick={() => handlePinnedChange(!isSelectionPinned)}
            title={
              isSelectionPinned
                ? "Allow the current selection to be replaced"
                : "Keep the current selection when selecting something else"
            }
            className={
              isSelectionPinned ? "text-primary" : "text-muted-foreground"
            }
          >
            <PushPinIcon
              weight={isSelectionPinned ? "fill" : "regular"}
              aria-hidden="true"
            />
            {isSelectionPinned ? "Pinned" : "Pin selection"}
          </Button>
        </div>
      </div>

      <div className="grid max-h-20 grid-cols-2 gap-1 overflow-y-auto pr-1">
        {effectiveFeatures.map((feature) => {
          const key = featureKey(feature);
          const name = feature.name || `Feature ${feature.featureId}`;
          const isInHostSelection = selectedKeys.has(key);

          return (
            <Chip
              key={key}
              variant="outline"
              size="xs"
              radius="circle"
              active={feature.pinned}
              showRemove
              removeLabel="Remove from context"
              onRemove={() => handleRemove(feature)}
              className={`h-6 w-full min-w-0 justify-start px-2 font-normal ${
                isInHostSelection || feature.pinned ? "" : "opacity-60"
              }`}
            >
              <span className="min-w-0 flex-1 truncate text-left" title={name}>
                {name}
                {feature.featureTypeName ? `: ${feature.featureTypeName}` : ""}
              </span>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-pressed={feature.pinned}
                aria-label={`${feature.pinned ? "Unpin" : "Pin"} ${name}`}
                title={feature.pinned ? "Unpin item" : "Pin item"}
                onClick={() => handleItemPinnedChange(feature, !feature.pinned)}
                className={`size-4 shrink-0 rounded-full p-0 hover:bg-foreground/10 ${
                  feature.pinned ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <PushPinIcon
                  weight={feature.pinned ? "fill" : "regular"}
                  aria-hidden="true"
                />
              </Button>
            </Chip>
          );
        })}
      </div>
    </section>
  );
}
